import { twMerge } from "tailwind-merge";
import { CheckCircle2 } from "lucide-react";

export interface TodoItem {
  id?: string;
  content: string;
  status: "pending" | "in_progress" | "completed";
}

/**
 * A `task` dispatch that references a todo (`todo_id`). Lets the list
 * show who's actually working an item — a todo with a running
 * delegation renders the agent's name and an active ring even if its own
 * status field still says "pending" (the status only flips to completed
 * when the task reports back).
 */
export interface TodoDelegation {
  todoId?: string;
  /** Friendly agent name, e.g. "Site builder". */
  agent: string;
  running: boolean;
}

export interface TodoListProps {
  todos: TodoItem[];
  delegations?: TodoDelegation[];
  className?: string;
}

/**
 * Compact inline todo list — the checklist the agent is working through.
 * Shows a status ring + content per item. Pending items sit quiet (muted
 * ring); items actually being worked — in_progress, or targeted by a
 * running delegation — get a primary ring and the agent's name.
 *
 * No per-item spinners: ten parallel items animating at once reads as
 * chaos and costs main-thread time; the surface's one spinner lives in
 * the ToolActivity/AgentActivity header.
 */
export function TodoList({ todos, delegations, className }: TodoListProps) {
  if (todos.length === 0) return null;

  const runningAgentByTodoId = new Map<string, string>();
  for (const d of delegations ?? []) {
    if (d.running && d.todoId) runningAgentByTodoId.set(d.todoId, d.agent);
  }

  return (
    <div
      className={twMerge("flex flex-col gap-1.5", className)}
      data-testid="todo-list"
    >
      {todos.map((todo, index) => {
        const agent = todo.id ? runningAgentByTodoId.get(todo.id) : undefined;
        const active = todo.status === "in_progress" || Boolean(agent);

        return (
          <div
            key={todo.id ?? index}
            className="flex items-center gap-2"
            data-testid="todo-item"
          >
            <div className="size-4 flex items-center justify-center flex-shrink-0">
              {todo.status === "completed" ? (
                <CheckCircle2 className="size-4 text-green-600" />
              ) : (
                <span
                  className={twMerge(
                    "size-3 rounded-full border",
                    active ? "border-primary-500 border-2" : "border-base-300"
                  )}
                />
              )}
            </div>
            <span
              className={twMerge(
                "text-xs",
                todo.status === "completed" ? "text-base-400" : "text-base-500"
              )}
            >
              {todo.content}
              {agent && (
                <span className="ml-1.5 text-base-400 italic">— {agent}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
